import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, StatusBar, Platform, AppState } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer, use$ } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import HomeScreen from './screens/HomeScreen';
import ChatScreen from './screens/ChatScreen';
import AuthScreen from './screens/AuthScreen';
import SettingsScreen from './screens/SettingsScreen';
import MessagesScreen from './screens/MessagesScreen';
import GalleryScreen from './screens/GalleryScreen';
import GamesScreen from './screens/GamesScreen';
import { Tables } from './utils/database.types';
import { currentUser$, initializeAuthListener, signInAnonymouslyUser } from './utils/Firebase';
import { loadAppState, loadUserProfile, setUserName, generateRandomUsername, appState$, checkStreak } from './utils/AppState';

type TabName = 'home' | 'req' | 'gallery' | 'messages' | 'settings';

const tabs: {
  key: TabName;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
}[] = [
  { key: 'home', label: 'Home', icon: 'home-outline' },
  { key: 'req', label: 'Req Image', icon: 'camera-outline' },
  { key: 'gallery', label: 'Gallery', icon: 'images-outline' },
  { key: 'messages', label: 'Messages', icon: 'chatbubble-outline' },
  { key: 'settings', label: 'Settings', icon: 'settings-outline' },
];

const App = observer(() => {
  const insets = useSafeAreaInsets();
  const [selectedCharacter, setSelectedCharacter] = useState<Tables<'characters'> | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabName>('home');
  const currentUser = use$(currentUser$);
  const isAuthenticated = !!currentUser;
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = AppState.addEventListener('change', (nextAppState) => {
        if (nextAppState === 'active') {
          checkStreak();
        }
      });
      return () => subscription.remove();
    }
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const style = document.createElement('style');
      style.textContent = `
        html, body, #root {
          height: 100vh;
          height: 100dvh;
          width: 100vw;
          width: 100dvw;
          overflow: hidden;
        }
      `;
      document.head.appendChild(style);
    }
    loadAppState();
    console.log('App: Initializing auth listener');
    initializeAuthListener();

    const unsubscribe = currentUser$.onChange(async (user) => {
      console.log('App: User changed', user);
      if (user) {
        await loadUserProfile();
        const currentUser = currentUser$.get();
        if (currentUser?.isAnonymous && !appState$.userName.get()) {
          const randomName = generateRandomUsername();
          setUserName(randomName);
        }
      }
      if (!user && authChecked) {
        console.log('App: No user, signing in anonymously');
        try {
          await signInAnonymouslyUser();
        } catch (error) {
          console.error('App: Anonymous sign-in failed:', error);
        }
      }
      setAuthChecked(true);
    });

    const timeout = setTimeout(() => {
      console.log('App: Auth check timeout - setting authChecked to true');
      setAuthChecked(true);
    }, 1000);

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [authChecked]);

  console.log('App: Rendering', { authChecked, isAuthenticated, selectedCharacter, currentUser });

  if (!authChecked) {
    console.log('App: Waiting for auth check');
    return null;
  }

  if (!isAuthenticated) {
    console.log('App: Showing AuthScreen');
    return <AuthScreen onAuthenticated={() => {}} />;
  }

  if (selectedCharacter) {
    return (
      <ChatScreen
        character={selectedCharacter}
        onBack={() => setSelectedCharacter(null)}
      />
    );
  }

  const renderTabScreen = () => {
    if (selectedTab === 'home') {
      return <HomeScreen onCharacterSelect={setSelectedCharacter} onPlayGames={() => setSelectedTab('req')} />;
    }

    if (selectedTab === 'req') {
      return <GamesScreen onBack={() => setSelectedTab('home')} />;
    }

    if (selectedTab === 'messages') {
      return <MessagesScreen onSelectCharacter={setSelectedCharacter} />;
    }

    if (selectedTab === 'gallery') {
      return <GalleryScreen />;
    }

    if (selectedTab === 'settings') {
      return <SettingsScreen />;
    }

    return (
      <View style={styles.placeholderScreen}>
        <Text style={styles.placeholderTitle}>{tabs.find((tab) => tab.key === selectedTab)?.label}</Text>
        <Text style={styles.placeholderText}>This section is coming soon.</Text>
      </View>
    );
  };

  return (
    <View style={styles.appContainer}>
      <StatusBar barStyle="light-content" backgroundColor="#080B1F" />

      <View style={styles.screenContainer}>{renderTabScreen()}</View>

      <View
        style={[
          styles.bottomNav,
          {
            paddingBottom: Math.max(insets.bottom, 10),
          },
        ]}
      >
        {tabs.map((tab) => {
          const isActive = selectedTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={styles.tabItem}
              onPress={() => setSelectedTab(tab.key)}
              activeOpacity={0.85}
            >
              <Ionicons
                name={isActive ? (tab.icon.replace('-outline', '') as keyof typeof Ionicons.glyphMap) : tab.icon}
                size={22}
                color={isActive ? '#DB47FF' : '#8D93B3'}
              />
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
});

const AppWrapper = () => {
  console.log('AppWrapper: Rendering');
  return (
    <SafeAreaProvider>
      <App />
    </SafeAreaProvider>
  );
};

const styles = StyleSheet.create({
  appContainer: {
    flex: 1,
    backgroundColor: '#080B1F',
    overflow: 'hidden',
  },
  screenContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  bottomNav: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    backgroundColor: '#0C112A',
    flexDirection: 'row',
    paddingTop: 10,
    paddingHorizontal: 6,
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  tabLabel: {
    fontSize: 12,
    color: '#8D93B3',
    fontWeight: '500',
  },
  tabLabelActive: {
    color: '#DB47FF',
    fontWeight: '700',
  },
  placeholderScreen: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
  },
  placeholderTitle: {
    color: '#F7F8FF',
    fontSize: 22,
    fontWeight: '700',
  },
  placeholderText: {
    color: '#9DA4C9',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default AppWrapper;
